import { useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";

const EMPTY = { name: "", email: "", phone: "", role: "customer", company_name: "", account_status: "pending", email_verified: false, partner_status: "pending", city: "", state: "", processes: "" };

export const AddAccountDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, processes: form.processes.split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await adminApi.post("/admin/accounts", payload);
      toast.success("Account created — share the invitation link.");
      setForm(EMPTY);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" strokeWidth={1.5} />Add account</DialogTitle>
          <DialogDescription>The user sets their own password through a one-time invitation link. Passwords are never typed or shown here.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label htmlFor="acc-name">Full name</Label><Input id="acc-name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="add-account-name" /></div>
            <div className="space-y-1"><Label htmlFor="acc-email">Email</Label><Input id="acc-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="add-account-email" /></div>
            <div className="space-y-1"><Label htmlFor="acc-phone">Mobile</Label><Input id="acc-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="add-account-phone" /></div>
            <div className="space-y-1"><Label>Account type</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="add-account-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer" data-testid="add-account-role-customer">Customer</SelectItem>
                  <SelectItem value="partner" data-testid="add-account-role-partner">Partner</SelectItem>
                  <SelectItem value="admin" data-testid="add-account-role-admin">VECTRON Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-2"><Label htmlFor="acc-company">Company</Label><Input id="acc-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="add-account-company" /></div>
            <div className="space-y-1"><Label>Account status</Label>
              <Select value={form.account_status} onValueChange={(v) => set("account_status", v)}>
                <SelectTrigger data-testid="add-account-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending" data-testid="add-account-status-pending">Pending (until password is set)</SelectItem>
                  <SelectItem value="active" data-testid="add-account-status-active">Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2 mt-5"><Label htmlFor="acc-verified" className="text-sm">Email verified</Label><Switch id="acc-verified" checked={form.email_verified} onCheckedChange={(v) => set("email_verified", v)} data-testid="add-account-verified" /></div>
          </div>
          {form.role === "partner" && (
            <div className="grid sm:grid-cols-2 gap-3 rounded-xl border border-dashed border-border p-3" data-testid="add-account-partner-fields">
              <div className="space-y-1"><Label>Partner approval</Label>
                <Select value={form.partner_status} onValueChange={(v) => set("partner_status", v)}>
                  <SelectTrigger data-testid="add-account-partner-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending review (existing workflow)</SelectItem>
                    <SelectItem value="approved">Approved now</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label htmlFor="acc-processes">Processes (comma separated)</Label><Input id="acc-processes" value={form.processes} onChange={(e) => set("processes", e.target.value)} placeholder="Sheet Metal, CNC Machining" data-testid="add-account-processes" /></div>
              <div className="space-y-1"><Label htmlFor="acc-city">City</Label><Input id="acc-city" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="add-account-city" /></div>
              <div className="space-y-1"><Label htmlFor="acc-state">State</Label><Input id="acc-state" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="add-account-state" /></div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} data-testid="add-account-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="add-account-submit">{busy ? "Creating…" : "Create & generate invite link"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
